import React, { useState, useEffect, useRef } from 'react';
import { useTheme } from '../theme/ThemeContext';
import { getTimeSeriesSliderTheme } from '../theme/themeStyles';

export interface WMSTimeSeriesSliderProps {
  dateArray: string[];
  /** Optional display labels (same length as dateArray); used for monthly CADI and pest-risk quarters. */
  dateLabels?: string[];
  layerType: 'yield' | 'vhi' | 'lst' | 'droneNDVI' | 'bsri' | 'cadi' | 'pestRisk';
  layerName: string;
  isVisible: boolean;
  onDateChange: (date: string, layerType: 'yield' | 'vhi' | 'lst' | 'droneNDVI' | 'bsri' | 'cadi' | 'pestRisk') => void;
  onPlayToggle?: (isPlaying: boolean, layerType: 'yield' | 'vhi' | 'lst' | 'droneNDVI' | 'bsri' | 'cadi' | 'pestRisk') => void;
  bsriData?: any;
  currentIndex?: number;
  onIndexChange?: (index: number) => void;
}

export default function WMSTimeSeriesSlider({
  dateArray = [],
  dateLabels,
  layerType,
  layerName,
  isVisible,
  onDateChange,
  onPlayToggle,
  bsriData,
  currentIndex: externalIndex,
  onIndexChange
}: WMSTimeSeriesSliderProps) {
  const { isDark } = useTheme();
  const ts = getTimeSeriesSliderTheme(isDark);

  const [internalIndex, setInternalIndex] = useState(0);
  const [isPlaying, setIsPlaying] = useState(false);
  const [playbackSpeed, setPlaybackSpeed] = useState(2000);
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const dateArrayRef = useRef<string[]>([]);
  const hasInitializedRef = useRef(false);

  const currentIndex = (layerType === 'bsri' || layerType === 'cadi' || layerType === 'pestRisk') && externalIndex !== undefined ? externalIndex : internalIndex;

  const formatDateLabel = (dateString: string, index: number) => {
    if (dateLabels && dateLabels[index]) return dateLabels[index];
    return formatDate(dateString);
  };

  const formatDate = (dateString: string) => {
    try {
      const monthOnly = dateString.match(/^(\d{4})-(\d{2})$/);
      if (monthOnly) {
        const date = new Date(Number(monthOnly[1]), Number(monthOnly[2]) - 1, 1);
        return date.toLocaleDateString('en-US', { year: 'numeric', month: 'short' });
      }
      const date = new Date(dateString);
      return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: '2-digit'
      });
    } catch {
      return dateString;
    }
  };

  const handleSliderChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const newIndex = parseInt(event.target.value);

    if (layerType === 'bsri' || layerType === 'cadi' || layerType === 'pestRisk') {
      if (onIndexChange) onIndexChange(newIndex);
    } else {
      setInternalIndex(newIndex);
      if (dateArray[newIndex]) {
        onDateChange(dateArray[newIndex], layerType);
      }
    }
  };

  const handlePlayToggle = () => {
    const newPlayState = !isPlaying;
    setIsPlaying(newPlayState);
    if (onPlayToggle) {
      onPlayToggle(newPlayState, layerType);
    }
  };

  const handleSpeedChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
    setPlaybackSpeed(parseInt(event.target.value));
  };

  useEffect(() => {
    if (isPlaying && dateArray.length > 0) {
      intervalRef.current = setInterval(() => {
        if ((layerType === 'bsri' || layerType === 'cadi' || layerType === 'pestRisk') && onIndexChange) {
          const nextIndex = (currentIndex + 1) % dateArray.length;
          onIndexChange(nextIndex);
        } else {
          setInternalIndex(prev => {
            const nextIndex = (prev + 1) % dateArray.length;
            if (dateArray[nextIndex]) {
              onDateChange(dateArray[nextIndex], layerType);
            }
            return nextIndex;
          });
        }
      }, playbackSpeed);
    } else if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isPlaying, playbackSpeed, dateArray.length, layerType, currentIndex]);

  useEffect(() => {
    if (layerType !== 'bsri' && layerType !== 'cadi' && layerType !== 'pestRisk') {
      const dateArrayStr = JSON.stringify(dateArray);
      const prevDateArrayStr = JSON.stringify(dateArrayRef.current);

      if (dateArrayStr !== prevDateArrayStr && dateArray.length > 0) {
        dateArrayRef.current = dateArray;
        setInternalIndex(0);
        setIsPlaying(false);
        if (!hasInitializedRef.current || dateArrayStr !== prevDateArrayStr) {
          onDateChange(dateArray[0], layerType);
          hasInitializedRef.current = true;
        }
      } else if (!hasInitializedRef.current && dateArray.length > 0) {
        dateArrayRef.current = dateArray;
        hasInitializedRef.current = true;
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dateArray, layerType]);

  if (!isVisible || dateArray.length === 0) {
    return null;
  }

  const currentDate = dateArray[currentIndex];
  const progress = dateArray.length > 1 ? (currentIndex / (dateArray.length - 1)) * 100 : 0;
  const rangeStyle = {
    background: `linear-gradient(to right, ${ts.rangeFill} 0%, ${ts.rangeFill} ${progress}%, ${ts.rangeTrack} ${progress}%, ${ts.rangeTrack} 100%)`,
  };

  return (
    <div className="absolute bottom-3 left-14 right-3 sm:left-[4.5rem] sm:right-[6rem] z-10">
      <div className={`w-full max-w-none ${ts.shell}`}>
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center space-x-2">
            <div className={ts.dot}></div>
            <h3 className={ts.title}>
              {layerName} Time Series
            </h3>
          </div>
          <div className={ts.counter}>
            {currentIndex + 1} of {dateArray.length}
          </div>
        </div>

        <div className="text-center mb-3">
          <div className={ts.subtitle}>
            {formatDateLabel(currentDate, currentIndex)}
          </div>
          {layerType === 'bsri' && bsriData && bsriData.tiles && bsriData.tiles[currentIndex] && (
            <div className="mt-1 flex items-center justify-center space-x-2">
              <span className={ts.bsriLabel}>Risk Index:</span>
              <span className={ts.bsriValue}>
                {bsriData.tiles[currentIndex].mean_rii.toFixed(3)}
              </span>
              {bsriData.tiles[currentIndex].week_number && (
                <span className={ts.bsriWeek}>
                  (Week {bsriData.tiles[currentIndex].week_number})
                </span>
              )}
            </div>
          )}
        </div>

        <div className="relative mb-4">
          <input
            type="range"
            min={0}
            max={Math.max(0, dateArray.length - 1)}
            value={currentIndex}
            onChange={handleSliderChange}
            className={ts.rangeClass}
            style={rangeStyle}
          />

          <div className="absolute top-0 left-0 w-full h-2 pointer-events-none">
            {dateArray.map((_, index) => (
              <div
                key={index}
                className={`${ts.marker} h-2`}
                style={{
                  left: `${dateArray.length > 1 ? (index / (dateArray.length - 1)) * 100 : 50}%`
                }}
              />
            ))}
          </div>

          <div className={`flex justify-between mt-1 ${ts.dateEdge}`}>
            <span>{formatDateLabel(dateArray[0], 0)}</span>
            <span>{formatDateLabel(dateArray[dateArray.length - 1], dateArray.length - 1)}</span>
          </div>
        </div>

        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-2">
            <button
              onClick={handlePlayToggle}
              className={`flex items-center justify-center w-10 h-10 rounded-full transition-colors ${ts.playCircle}`}
              title={isPlaying ? 'Pause' : 'Play'}
            >
              {isPlaying ? (
                <i className="ri-pause-fill text-lg"></i>
              ) : (
                <i className="ri-play-fill text-lg ml-0.5"></i>
              )}
            </button>

            <button
              onClick={() => {
                const newIndex = Math.max(0, currentIndex - 1);
                if ((layerType === 'bsri' || layerType === 'cadi' || layerType === 'pestRisk') && onIndexChange) {
                  onIndexChange(newIndex);
                } else {
                  setInternalIndex(newIndex);
                  if (dateArray[newIndex]) {
                    onDateChange(dateArray[newIndex], layerType);
                  }
                }
              }}
              disabled={currentIndex === 0}
              className={`flex items-center justify-center w-8 h-8 rounded transition-colors ${ts.navBtn}`}
              title="Previous"
            >
              <i className="ri-skip-back-fill text-sm"></i>
            </button>

            <button
              onClick={() => {
                const newIndex = Math.min(dateArray.length - 1, currentIndex + 1);
                if ((layerType === 'bsri' || layerType === 'cadi' || layerType === 'pestRisk') && onIndexChange) {
                  onIndexChange(newIndex);
                } else {
                  setInternalIndex(newIndex);
                  if (dateArray[newIndex]) {
                    onDateChange(dateArray[newIndex], layerType);
                  }
                }
              }}
              disabled={currentIndex === dateArray.length - 1}
              className={`flex items-center justify-center w-8 h-8 rounded transition-colors ${ts.navBtn}`}
              title="Next"
            >
              <i className="ri-skip-forward-fill text-sm"></i>
            </button>
          </div>

          <div className="flex items-center space-x-2">
            <label className={ts.speedLabel}>Speed:</label>
            <select
              value={playbackSpeed}
              onChange={handleSpeedChange}
              className={ts.speedSelect}
            >
              <option value={3333}>0.3x</option>
              <option value={2000}>0.5x</option>
              <option value={1000}>1x</option>
              <option value={500}>2x</option>
              <option value={250}>4x</option>
            </select>
          </div>
        </div>

        <div className="mt-3">
          <div className={ts.progressTrack}>
            <div
              className={ts.progressBar}
              style={{ width: `${progress}%` }}
            ></div>
          </div>
        </div>
      </div>
    </div>
  );
}
